/**
 * B54 — deterministic vertical scroll reset at a route-entry boundary.
 *
 * Founder report (on-device, 2026-08-29): "when opening the season plan and
 * selecting a week, the view loads a bit lower than the title which is
 * confusing." Entering `/v2/plan/week/:n` landed the reader part-way down the
 * page with the RouteHero title already scrolled off the top.
 *
 * Two mechanisms produced that, and this helper closes the first:
 *
 *   1. **SPA scroll carry-over.** React Router's `<Routes>` does not reset the
 *      document scroll on navigation, and this app deliberately has no global
 *      scroll-restoration wrapper (see `resetHorizontalScroll` — B-15 — which
 *      established the convention: a small imperative `src/lib/` helper called
 *      from a route-entry effect, at KNOWN boundaries only, never a watchdog).
 *      The season plan at `/v2/plan` is a long list; the reader scrolls down to
 *      find week 9, taps it, and the week route mounts with the document still
 *      parked at the season list's offset.
 *
 *   2. The route's own "B-22" anchor, which scrolled the week's main column to
 *      `block: 'start'` — deliberately putting the hero title ABOVE the
 *      viewport. Removed in routes/plan-week.tsx alongside this helper.
 *
 * Axis discipline mirrors B-15's, inverted: this owns the VERTICAL axis and
 * preserves the horizontal position (B-2/B-15 own that one — clearing it here
 * would duplicate their boundary and mask a stale WKWebView pan they exist to
 * catch).
 *
 * Motion: the landing is INSTANT by construction. `behavior: 'auto'` is passed
 * explicitly so a future `scroll-behavior: smooth` in CSS cannot turn a
 * navigation landing into an animated flourish, and the direct `scrollTop`
 * writes below are already non-animated. That makes the helper correct under
 * `prefers-reduced-motion: reduce` without needing to branch on it — there is
 * no animation to reduce.
 */
export function scrollRouteToTop(): void {
  if (typeof document === 'undefined') return;
  const scroller = document.scrollingElement ?? document.documentElement;
  if (scroller && scroller.scrollTop !== 0) scroller.scrollTop = 0;
  // Some WebKit states scroll body instead of the documentElement.
  if (document.body && document.body.scrollTop !== 0) document.body.scrollTop = 0;
  // Belt: window-level scroll. Horizontal position is deliberately preserved.
  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
    try {
      window.scrollTo({ left: window.scrollX ?? 0, top: 0, behavior: 'auto' });
    } catch {
      // Older WebKit without options-object support.
      window.scrollTo(window.scrollX ?? 0, 0);
    }
  }
}
